/**
 * MutationQueueContext - Gestisce lo stato globale della coda di sincronizzazione
 *
 * Funzionalita:
 * - Traccia il numero di operazioni in coda
 * - Traccia lo stato di sincronizzazione
 * - Auto-sync quando si torna online
 * - Notifiche toast su completamento/errore
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useOfflineStorageV2 } from './useOfflineStorageV2';
import { useOfflinePreloader } from './useOfflinePreloader';
import { useToast } from '@/shared/hooks/use-toast';

export interface SyncResult {
  success: number;
  failed: number;
  errors: string[];
}

export interface MutationQueueContextValue {
  // Stato
  pendingCount: number;
  isSyncing: boolean;
  isPreloading: boolean;
  lastSyncTime: Date | null;
  lastSyncResult: SyncResult | null;
  isOnline: boolean;

  // Progress preload
  preloadProgress: {
    stage: string;
    current: number;
    total: number;
    message: string;
  };

  // Azioni
  triggerSync: () => Promise<void>;
  triggerPreload: () => void;
  startPreload: () => Promise<void>;
  cancelPreload: () => void;
}

const MutationQueueContext = createContext<MutationQueueContextValue | null>(null);

export const useMutationQueue = () => {
  const context = useContext(MutationQueueContext);
  if (!context) {
    throw new Error('useMutationQueue must be used within MutationQueueProvider');
  }
  return context;
};

interface MutationQueueProviderProps {
  children: React.ReactNode;
  autoSyncOnOnline?: boolean;
  syncDebounceMs?: number;
}

export const MutationQueueProvider: React.FC<MutationQueueProviderProps> = ({
  children,
  autoSyncOnOnline = true,
  syncDebounceMs = 2000,
}) => {
  const { toast } = useToast();
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const wasOfflineRef = useRef(false);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Hook per storage offline
  const {
    isOnline,
    isSyncing,
    pendingCount,
    lastSyncTime: storageSyncTime,
    syncPendingOperations,
  } = useOfflineStorageV2();

  // Hook per preloader
  const {
    progress: preloadProgress,
    isPreloading,
    startPreload,
    triggerPreload,
    cancelPreload,
  } = useOfflinePreloader();

  // Aggiorna lastSyncTime dallo storage
  useEffect(() => {
    if (storageSyncTime) {
      setLastSyncTime(new Date(storageSyncTime));
    }
  }, [storageSyncTime]);

  // Traccia transizioni offline -> online
  useEffect(() => {
    if (!isOnline) {
      wasOfflineRef.current = true;
    }
  }, [isOnline]);

  // Auto-sync quando si torna online
  useEffect(() => {
    if (isOnline && wasOfflineRef.current && autoSyncOnOnline && pendingCount > 0) {
      wasOfflineRef.current = false;

      // Debounce per evitare sync multipli ravvicinati
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      syncTimeoutRef.current = setTimeout(async () => {
        toast({
          title: 'Connessione ripristinata',
          description: `Sincronizzando ${pendingCount} operazioni...`,
        });

        try {
          await syncPendingOperations();
          toast({
            title: 'Sincronizzazione completata',
            description: 'Tutte le operazioni sono state sincronizzate.',
          });
        } catch (error) {
          console.error('[MutationQueueContext] Sync error:', error);
          toast({
            title: 'Errore di sincronizzazione',
            description: 'Alcune operazioni non sono state sincronizzate. Riprova piu tardi.',
            variant: 'destructive',
          });
        }
      }, syncDebounceMs);
    }

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [isOnline, autoSyncOnOnline, pendingCount, syncPendingOperations, toast, syncDebounceMs]);

  // Notifica quando si va offline
  useEffect(() => {
    if (!isOnline && pendingCount > 0) {
      toast({
        title: 'Modalita offline',
        description: `${pendingCount} operazioni in coda. Verranno sincronizzate quando tornerai online.`,
      });
    }
  }, [isOnline, pendingCount, toast]);

  // Trigger sync manuale
  const triggerSync = useCallback(async () => {
    if (!isOnline) {
      toast({
        title: 'Impossibile sincronizzare',
        description: 'Sei offline. La sincronizzazione avverra automaticamente quando tornerai online.',
        variant: 'destructive',
      });
      return;
    }

    if (isSyncing) {
      console.warn('[MutationQueueContext] Sync already in progress');
      return;
    }

    if (pendingCount === 0) {
      toast({
        title: 'Nulla da sincronizzare',
        description: 'Non ci sono operazioni in coda.',
      });
      return;
    }

    try {
      await syncPendingOperations();
      setLastSyncTime(new Date());
      setLastSyncResult({ success: pendingCount, failed: 0, errors: [] });

      toast({
        title: 'Sincronizzazione completata',
        description: 'Tutte le operazioni sono state sincronizzate con successo.',
      });
    } catch (error: any) {
      console.error('[MutationQueueContext] Manual sync error:', error);
      setLastSyncResult({
        success: 0,
        failed: pendingCount,
        errors: [error.message],
      });

      toast({
        title: 'Errore di sincronizzazione',
        description: error.message || 'Errore durante la sincronizzazione.',
        variant: 'destructive',
      });
    }
  }, [isOnline, isSyncing, pendingCount, syncPendingOperations, toast]);

  // Context value
  const value: MutationQueueContextValue = {
    // Stato
    pendingCount,
    isSyncing,
    isPreloading,
    lastSyncTime,
    lastSyncResult,
    isOnline,

    // Progress preload
    preloadProgress: {
      stage: preloadProgress.stage,
      current: preloadProgress.current,
      total: preloadProgress.total,
      message: preloadProgress.message,
    },

    // Azioni
    triggerSync,
    triggerPreload,
    startPreload,
    cancelPreload,
  };

  return (
    <MutationQueueContext.Provider value={value}>
      {children}
    </MutationQueueContext.Provider>
  );
};

export default MutationQueueProvider;
